import { isViewsEntityParam, viewsEntityLabel } from '@/clients/views-entities'

export const APP_TITLE = 'Moment'

/**
 * Route patterns and the section name each shows in the browser tab. `:` matches
 * one path segment and a trailing `*` matches the rest. First match wins, so a
 * literal segment goes above the `:` pattern it would otherwise fall into.
 */
const ROUTE_TITLES: readonly (readonly [pattern: string, title: string])[] = [
  ['/agent', 'Agent'],
  ['/agent/*', 'Agent'],
  ['/opportunities', 'Opportunities'],
  ['/ai/controls', 'AI controls'],

  ['/marketplace', 'Marketplace'],
  ['/instruments/:id', 'Instrument'],
  ['/fixed-income-hub', 'Tasks hub'],
  ['/fixed-income-hub/*', 'Tasks hub'],
  ['/research/discover', 'Discover'],
  ['/research/fixed-income', 'Bond screener'],
  ['/research/bond-screener', 'Bond screener'],
  ['/research/fixed-income/:id', 'Bond'],
  ['/research/fixed-income/:id/preview', 'Bond'],
  ['/research/bond-screener/:id/preview', 'Bond'],
  ['/research/:slug', 'Research'],

  ['/orders/trade-blotter', 'Orders'],
  ['/orders/historical-orders', 'Historical orders'],
  ['/orders/create', 'New order'],
  ['/order-entry', 'Order entry'],
  ['/blotter', 'Blotter'],
  ['/blotter/group-orders', 'Group orders'],
  ['/blotter/group-orders/*', 'Group orders'],
  ['/blotter/bulk-upload/:id', 'Bulk upload'],
  ['/blotter/:orderId', 'Order'],
  ['/trade-desk/trade-blotter', 'Trade blotter'],
  ['/trade-desk/trade-blotter/bulk-upload', 'Bulk upload'],
  ['/trade-desk/historical-orders', 'Historical orders'],
  ['/trade-desk/risk-controls', 'Risk controls'],
  ['/trade-desk/attestations', 'Attestations'],
  ['/trade-desk/execution-rules', 'Execution rules'],
  ['/markup-editor', 'Markup editor'],
  ['/rep-fee-editor', 'Rep fee editor'],
  ['/trade-plan/:id', 'Trade plan'],

  ['/accounts', 'Accounts'],
  ['/accounts/holdings', 'Holdings'],
  ['/accounts/:id', 'Account'],
  ['/accounts/:id/sleeves/create', 'New sleeve'],
  ['/accounts/:id/sleeves/:sleeveId', 'Sleeve'],
  ['/accounts/:id/sleeves/:sleeveId/edit', 'Edit sleeve'],
  ['/account-groups/:id', 'Account group'],

  ['/proposals', 'Proposals'],
  ['/proposals/v2/:id', 'Proposal'],
  ['/proposals/v2/:id/table', 'Proposal'],
  ['/proposals/:id', 'Proposal'],
  ['/proposals/:id/review', 'Proposal review'],
  ['/models', 'Models'],
  ['/models/new', 'Model builder'],
  ['/models/:id', 'Model'],
  ['/models/:id/edit', 'Edit model'],
  ['/strategies', 'Strategies'],
  ['/strategies/new/*', 'New strategy'],
  ['/strategies/:id', 'Strategy'],
  ['/strategies/:id/edit', 'Edit strategy'],
  ['/lists', 'Filter lists'],
  ['/lists/:id', 'Filter list'],
  ['/allocations', 'Allocation tool'],
  ['/allocations/run', 'Allocation'],
  ['/allocation-templates/new', 'New allocation template'],
  ['/allocation-templates/:id', 'Allocation template'],
  ['/allocation-templates/:id/edit', 'Edit allocation template'],

  ['/origination/:id', 'New issue'],
  ['/offerings', 'Offerings'],
  ['/ipo-management', 'IPO management'],
  ['/ipo-management/:id', 'IPO management'],
  ['/ipo-marketplace', 'IPO marketplace'],
  ['/ipo-marketplace/:id', 'IPO marketplace'],

  ['/education', 'Education'],
  ['/education/courses', 'Courses'],
  ['/education/*', 'Education'],
  ['/custom-apps/:appKey', 'App'],

  ['/help', 'Help center'],
  ['/help/*', 'Help center'],

  ['/settings/users', 'Users'],
  ['/settings/roles', 'Roles'],
  ['/settings/teams', 'Teams'],
  ['/settings/tags', 'Tags'],
  ['/settings/profile', 'Profile'],
  ['/settings/profile/trader-profile', 'Trader profile'],
  ['/settings/organizations/*', 'Organization'],

  ['/login', 'Log in'],
  ['/logout', 'Log out'],
  ['/403', 'Access denied'],
  ['/error', 'Error'],
]

function segments(path: string): string[] {
  return path.split('/').filter((segment) => segment !== '')
}

function matches(pattern: readonly string[], path: readonly string[]): boolean {
  for (const [index, part] of pattern.entries()) {
    if (part === '*') {
      return path.length > index
    }
    const segment = path[index]
    if (segment === undefined || (part[0] !== ':' && part !== segment)) {
      return false
    }
  }
  return pattern.length === path.length
}

const COMPILED_ROUTE_TITLES = ROUTE_TITLES.map(
  ([pattern, title]) => [segments(pattern), title] as const,
)

/** The section name for the page at `pathname`, or `null` for a route with none. */
export function routeTitle(
  pathname: string,
  searchParams: Pick<URLSearchParams, 'get'>,
): string | null {
  const path = segments(pathname)
  if (path.length === 1 && path[0] === 'views') {
    const entity = searchParams.get('entity') ?? undefined
    return isViewsEntityParam(entity) ? viewsEntityLabel(entity) : 'Views'
  }
  const match = COMPILED_ROUTE_TITLES.find(([pattern]) =>
    matches(pattern, path),
  )
  return match?.[1] ?? null
}

/** `<page> · <section> · Moment`, dropping the parts the page does not have. */
export function documentTitle(
  page: string | null,
  section: string | null,
): string {
  const parts: string[] = []
  if (page !== null) {
    parts.push(page)
  }
  if (section !== null && section !== page) {
    parts.push(section)
  }
  parts.push(APP_TITLE)
  return parts.join(' · ')
}
